import React from 'react';
import { Top3Dimension, WidgetSettings } from '../types/quiz';

interface Top3AreasSectionProps {
  top3Dimensions: Top3Dimension[];
  settings: WidgetSettings;
}

const DIMENSION_COLORS: Record<string, string> = {
  confidence: '#3B82F6',  // Blue - Emotional Safety
  pacing: '#8B5CF6',      // Purple - Individualized Pacing
  structure: '#6366F1',   // Indigo - Structure & Predictability
  sensory: '#14B8A6',     // Teal - Sensory Support
  interest: '#F97316'     // Orange - Interest-Driven Learning
};

const Top3AreasSection: React.FC<Top3AreasSectionProps> = ({ top3Dimensions, settings }) => {
  const accentColor = settings.accent_color || '#10B981';

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-7 mb-6">
      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4 md:mb-6">
        Your Top 3 Areas
      </h2>

      <div className="space-y-5 md:space-y-6">
        {top3Dimensions.map((dimension, index) => {
          const dimensionColor = DIMENSION_COLORS[dimension.dimension] || '#6B7280';
          const hexToRgb = (hex: string) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '107, 114, 128';
          };
          const rgbColor = hexToRgb(dimensionColor);

          return (
            <div
              key={dimension.dimension}
              className="rounded-lg p-5 md:p-6 border-l-4"
              style={{
                borderLeftColor: dimensionColor,
                backgroundColor: `rgba(${rgbColor}, 0.05)`
              }}
            >
              {/* Dimension Header */}
              <div className="mb-3 md:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5">
                  {index + 1}. {dimension.insights.dimension_name}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  Score: {dimension.score}/{dimension.possible} ({dimension.percentage}%)
                </p>
              </div>

            {/* Insights */}
            <div className="space-y-3 md:space-y-4">
              {/* Daily Reality */}
              <div className="flex items-start gap-2.5 md:gap-3">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                  style={{ color: accentColor }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 text-sm md:text-base mb-1">Daily Reality</p>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {dimension.insights.daily_reality.split('\n\n').map((paragraph: string, i: number, arr: string[]) => (
                      <span key={i} className={`block ${i < arr.length - 1 ? 'mb-3' : ''}`}>
                        {paragraph}
                      </span>
                    ))}
                  </p>
                </div>
              </div>

              {/* Misunderstood As */}
              <div className="flex items-start gap-2.5 md:gap-3">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                  style={{ color: accentColor }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 text-sm md:text-base mb-1">Misunderstood As</p>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {dimension.insights.misunderstood_as.split('\n\n').map((paragraph: string, i: number, arr: string[]) => (
                      <span key={i} className={`block ${i < arr.length - 1 ? 'mb-3' : ''}`}>
                        {paragraph}
                      </span>
                    ))}
                  </p>
                </div>
              </div>

              {/* What Works */}
              <div className="flex items-start gap-2.5 md:gap-3">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                  style={{ color: accentColor }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 text-sm md:text-base mb-1">What Works</p>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {dimension.insights.what_works.split('\n\n').map((paragraph: string, i: number, arr: string[]) => (
                      <span key={i} className={`block ${i < arr.length - 1 ? 'mb-3' : ''}`}>
                        {paragraph}
                      </span>
                    ))}
                  </p>
                </div>
              </div>

              {/* Common Mistakes to Avoid */}
              <div className="flex items-start gap-2.5 md:gap-3">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  style={{ color: '#F59E0B' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 text-sm md:text-base mb-1">Common Mistakes to Avoid</p>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {dimension.insights.what_doesnt_work.split('\n').map((line: string, i: number) => (
                      <span key={i}>• {line}<br /></span>
                    ))}
                  </p>
                </div>
              </div>

              {/* Red Flag */}
              <div className="flex items-start gap-2.5 md:gap-3">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  style={{ color: '#EF4444' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-semibold text-red-600 text-sm md:text-base mb-1">Red Flag</p>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {dimension.insights.red_flag.split('\n\n').map((paragraph: string, i: number, arr: string[]) => (
                      <span key={i} className={`block ${i < arr.length - 1 ? 'mb-3' : ''}`}>
                        {paragraph}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Top3AreasSection;
